"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../../elements/button/button";
import { TextD2C } from "../../elements/text-d2c/text";
import { cn } from "../../lib/utils";
import type { MainNavigationColumn } from "../main-navigation/main-navigation.types";
import { footerClasses } from "./footer.constants";
import type {
  FooterCountryLink,
  FooterCta,
  FooterPaymentMethod,
  FooterSocialLink,
} from "./footer.types";
import { FooterColumn } from "./footer-column/footer-column";
import { FooterPaymentMethods } from "./footer-payment-methods/footer-payment-methods";
import { FooterSocialLinks } from "./footer-social-links/footer-social-links";

export interface FooterProps {
  /** Brand logo, usually wrapped in a link to the homepage. */
  logo: ReactNode;
  /** Link columns of the footer navigation, e.g. Products or Service. */
  columns: MainNavigationColumn[];
  /** Columns of the footer navigation with a text, e.g. the headquarters. */
  addresses?: MainNavigationColumn[];
  /** Newsletter sign-up at the start of the first row. */
  newsletter?: FooterCta;
  /** Calls to action next to the newsletter, e.g. Find a dealer. */
  ctas?: FooterCta[];
  socialLinks?: FooterSocialLink[];
  paymentMethods?: FooterPaymentMethod[];
  country?: FooterCountryLink;
  microcopies: {
    /** Accessible name of the footer navigation. */
    navigationLabel: string;
    /** Accessible name of the social media links, e.g. "Follow us". */
    socialLinks: string;
    /** Accessible name of the payment method badges. */
    paymentMethods: string;
    /** Prefix of the country link's name, e.g. "Change country: United States". */
    country: string;
  };
}

/**
 * D2C site footer. The first row holds the newsletter and further calls to
 * action, followed by the footer navigation's link columns and addresses,
 * the social media links, the logo, the payment methods and the country.
 * Below the desktop breakpoint the link columns open and close on their
 * headline; the first-row calls to action and the social media links stack
 * and stay visible.
 */
export function Footer(props: Readonly<FooterProps>) {
  const addresses = props.addresses ?? [];
  const ctas = props.ctas ?? [];
  const socialLinks = props.socialLinks ?? [];
  const paymentMethods = props.paymentMethods ?? [];

  return (
    <footer className="component-footer bg-black text-white">
      <div className="container--fluid flex flex-col gap-10 pt-10 pb-4 lg:gap-15 lg:pt-20 lg:pb-10">
        {props.newsletter || ctas.length > 0 ? (
          <div className="grid gap-unit border-alabastergrey/30 border-b pb-10 lg:grid-cols-12 lg:pb-15">
            {props.newsletter ? (
              <FooterCtaBlock
                className="lg:col-span-4"
                cta={props.newsletter}
              />
            ) : null}
            {ctas.map((cta) => (
              <FooterCtaBlock
                className="lg:col-span-4"
                cta={cta}
                key={cta.id}
              />
            ))}
          </div>
        ) : null}

        {/* The mobile design shows the logo above the navigation. */}
        <div className="flex justify-center lg:hidden">{props.logo}</div>

        <nav
          aria-label={props.microcopies.navigationLabel}
          className="flex flex-col gap-10 max-lg:px-6 lg:grid lg:auto-cols-fr lg:grid-flow-col lg:gap-unit"
        >
          {props.columns.map((column) => (
            <FooterColumn column={column} key={column.id} variant="links" />
          ))}
          {addresses.length > 0 ? (
            <div className="flex flex-col gap-6 max-lg:mt-4 lg:gap-15">
              {addresses.map((address) => (
                <FooterColumn
                  column={address}
                  key={address.id}
                  variant="address"
                />
              ))}
            </div>
          ) : null}
        </nav>

        {socialLinks.length > 0 ? (
          <FooterSocialLinks
            label={props.microcopies.socialLinks}
            links={socialLinks}
          />
        ) : null}

        <div className="hidden justify-center lg:flex">{props.logo}</div>

        {paymentMethods.length > 0 || props.country ? (
          <div className="flex flex-col items-center gap-10 pt-5 lg:flex-row lg:items-end lg:justify-between lg:pt-0">
            {paymentMethods.length > 0 ? (
              <FooterPaymentMethods
                label={props.microcopies.paymentMethods}
                methods={paymentMethods}
              />
            ) : null}
            {props.country ? (
              <Button
                aria-label={`${props.microcopies.country}: ${props.country.label}`}
                className={cn(
                  footerClasses.focusRing,
                  "flex items-center gap-[3px] text-[10px] uppercase leading-3 tracking-[0.06em] hover:underline lg:ml-auto"
                )}
                href={props.country.href}
                mode="D2C"
                onClick={props.country.onClick}
                target={props.country.target}
                unstyled={true}
              >
                {/* Keeps the row as low as the text, which the design aligns
                    with the bottom of the payment badges. */}
                <ChevronDown aria-hidden="true" className="-my-1" />
                {props.country.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </footer>
  );
}

function FooterCtaBlock(
  props: Readonly<{
    cta: FooterCta;
    className?: string;
  }>
) {
  return (
    <div className={cn("flex flex-col items-start gap-4", props.className)}>
      <TextD2C color="white" style="no-mb" tagName="h2" variant="h6">
        {props.cta.headline}
      </TextD2C>
      <Button
        className={cn(
          footerClasses.button,
          // "capitalize" (the default) renders the label exactly as authored.
          props.cta.labelCasing === "uppercase" ? "uppercase" : "normal-case"
        )}
        href={props.cta.link.href}
        mode="D2C"
        size="sm"
        target={props.cta.link.target}
        variant="outline"
      >
        {props.cta.link.label}
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  );
}
